import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPen,
  faMinus,
  faEyeSlash,
  faExclamationTriangle,
  faTruck,
  faUser,
  faChevronDown,
  faChevronRight,
  faLink,
} from '@fortawesome/free-solid-svg-icons';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import GenericModal from '../../../components/agv-generic-modal';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const MAX_DISPLAYED_ISSUES = 20;

/**
 * Erreurs ou avertissements ligne à ligne d'un import (import de tournées au
 * format standard : { row, column, code, message })
 */
function ImportIssuesList({ title, issues, tone }) {
  if (!Array.isArray(issues) || issues.length === 0) return null;

  const colors =
    tone === 'error'
      ? 'tw-bg-red-50 tw-border-red-200 tw-text-red-700'
      : 'tw-bg-amber-50 tw-border-amber-200 tw-text-amber-800';

  return (
    <div
      className={`tw-w-full tw-rounded-lg tw-border tw-border-solid tw-p-3 tw-text-left tw-text-sm ${colors}`}
    >
      <div className="tw-font-semibold tw-mb-1">
        {title} · {issues.length}
      </div>
      <ul className="tw-m-0 tw-pl-4 tw-max-h-40 tw-overflow-y-auto">
        {issues.slice(0, MAX_DISPLAYED_ISSUES).map((issue, index) => (
          <li key={index} title={issue?.code}>
            {typeof issue === 'string' ? issue : issue?.message}
          </li>
        ))}
      </ul>
      {issues.length > MAX_DISPLAYED_ISSUES && (
        <div className="tw-mt-1">
          … et {issues.length - MAX_DISPLAYED_ISSUES} autre(s)
        </div>
      )}
    </div>
  );
}

export default function AddFileUploadSuccessModal({
  isOpen,
  setIsOpen,
  isLoading,
  uploadResult,
  file,
}) {
  const BASE_URL = useSelector((state) => state.BASE_URL);
  const [error, setError] = useState(null);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [assignments, setAssignments] = useState({});
  const [isResolvingConflicts, setIsResolvingConflicts] = useState(false);
  const [resolvedResult, setResolvedResult] = useState(null);
  const [expandedClients, setExpandedClients] = useState({});
  const navigate = useNavigate();

  // Extraire les conflits de uploadResult avec useMemo pour éviter les re-renders
  const conflicts = useMemo(
    () => uploadResult?.conflicts || [],
    [uploadResult]
  );
  const hasConflicts =
    uploadResult?.requires_manual_assignment && conflicts.length > 0;
  const hasResolvedResult = resolvedResult !== null;

  // Reset les states à la fermeture de la modale
  useEffect(() => {
    if (!isOpen) {
      setAssignments({});
      setResolvedResult(null);
      setError(null);
      setIsResolvingConflicts(false);
      setIsFinalizing(false);
      setExpandedClients({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (!hasConflicts || conflicts.length === 0) return;

    const initialAssignments = {};
    conflicts.forEach((conflict) => {
      initialAssignments[conflict.tour_key] = {};
    });
    setAssignments(initialAssignments);
  }, [hasConflicts, conflicts]);

  const handleAssignmentChange = (tourKey, clientKey, newTourKey) => {
    setAssignments((prev) => ({
      ...prev,
      [tourKey]: {
        ...prev[tourKey],
        [clientKey]: newTourKey,
      },
    }));
  };

  const toggleClientExpanded = (tourKey, clientKey) => {
    const key = `${tourKey}_${clientKey}`;
    setExpandedClients((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Vérifier si toutes les assignations sont valides
  const getAssignmentValidation = () => {
    if (!hasConflicts) return { isValid: true, error: null };

    for (const conflict of conflicts) {
      const tourAssignments = assignments[conflict.tour_key] || {};
      const clientKeys = conflict.clients.map((c) => c.client_key);

      // Vérifier que tous les clients ont une assignation
      for (const clientKey of clientKeys) {
        if (!tourAssignments[clientKey]) {
          return {
            isValid: false,
            error: 'Tous les clients doivent être assignés à une tournée.',
          };
        }
      }

      // Vérifier les doublons d'ordre sur une même tournée
      const tourAssignmentsByOrder = {};
      conflict.clients.forEach((client) => {
        const assignedTour = tourAssignments[client.client_key];
        if (assignedTour) {
          if (!tourAssignmentsByOrder[assignedTour]) {
            tourAssignmentsByOrder[assignedTour] = [];
          }
          tourAssignmentsByOrder[assignedTour].push(client.order);
        }
      });

      const hasDuplicateOrders = Object.values(tourAssignmentsByOrder).some(
        (orders) => new Set(orders).size !== orders.length
      );

      if (hasDuplicateOrders) {
        return {
          isValid: false,
          error:
            'Plusieurs clients avec le même ordre assignés à la même tournée.',
        };
      }
    }

    return { isValid: true, error: null };
  };

  const handleResolveConflicts = () => {
    setIsResolvingConflicts(true);
    setError(null);

    // Nettoyer les assignations : ne garder que les clients "root" (pas les attachés)
    const cleanedAssignments = {};
    conflicts.forEach((conflict) => {
      const tourAssignments = assignments[conflict.tour_key];
      if (tourAssignments) {
        cleanedAssignments[conflict.tour_key] = {};
        // Ne garder que les assignations des clients qui sont dans conflict.clients
        conflict.clients.forEach((client) => {
          if (tourAssignments[client.client_key]) {
            cleanedAssignments[conflict.tour_key][client.client_key] =
              tourAssignments[client.client_key];
          }
        });
      }
    });

    const token = localStorage.getItem('fleetToken');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('dry_run', '1');
    formData.append('manual_assignments', JSON.stringify(cleanedAssignments));

    axios
      .post(BASE_URL + '/tours/upload', formData, {
        headers: {
          'content-type': 'application/x-www-form-urlencoded',
          Authorization: token,
        },
      })
      .then((res) => {
        setResolvedResult(res.data);
      })
      .catch((err) => {
        setError(
          err.response?.data?.message ||
            'Une erreur est survenue lors de la résolution des conflits.'
        );
      })
      .finally(() => {
        setIsResolvingConflicts(false);
      });
  };

  const handleFinalizeImport = () => {
    setIsFinalizing(true);
    setError(null);

    const token = localStorage.getItem('fleetToken');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('dry_run', '0');

    if (assignments && Object.keys(assignments).length > 0) {
      // Nettoyer les assignations : ne garder que les clients "root" (pas les attachés)
      const cleanedAssignments = {};
      conflicts.forEach((conflict) => {
        const tourAssignments = assignments[conflict.tour_key];
        if (tourAssignments) {
          cleanedAssignments[conflict.tour_key] = {};
          // Ne garder que les assignations des clients qui sont dans conflict.clients
          conflict.clients.forEach((client) => {
            if (tourAssignments[client.client_key]) {
              cleanedAssignments[conflict.tour_key][client.client_key] =
                tourAssignments[client.client_key];
            }
          });
        }
      });
      formData.append('manual_assignments', JSON.stringify(cleanedAssignments));
    }

    axios
      .post(BASE_URL + '/tours/upload', formData, {
        headers: {
          'content-type': 'application/x-www-form-urlencoded',
          Authorization: token,
        },
      })
      .then((res) => {
        if (res.status === 200) {
          setIsOpen(false);
          navigate('/tours?limit=10&offset=0&query=&orderBy=DESC');
        } else {
          setError("Une erreur est survenue lors de l'importation.");
        }
      })
      .catch(() => {
        setError("Une erreur est survenue lors de l'importation.");
      })
      .finally(() => {
        setIsFinalizing(false);
      });
  };

  // Déterminer quel mode afficher
  const showConflictResolution = hasConflicts && !hasResolvedResult;
  const showResolvedSummary = hasConflicts && hasResolvedResult;
  const showNormalImport = !hasConflicts;

  return (
    <GenericModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={
        showConflictResolution
          ? 'Résolution des conflits de tournées'
          : 'Importation des tournées'
      }
      mainActionText={
        showConflictResolution
          ? isResolvingConflicts
            ? 'Traitement...'
            : 'Valider et continuer'
          : isFinalizing
            ? 'Chargement...'
            : 'Importer définitivement'
      }
      mainActionIsLoading={isLoading || isFinalizing || isResolvingConflicts}
      mainAction={
        showConflictResolution ? handleResolveConflicts : handleFinalizeImport
      }
      mainActionDisabled={
        showConflictResolution && !getAssignmentValidation().isValid
      }
      size={showConflictResolution ? 'tw-max-w-3xl' : 'tw-max-w-xl'}
    >
      <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
        {showConflictResolution ? (
          <>
            <div className="tw-flex tw-flex-row tw-items-start tw-gap-2 tw-bg-yellow-50 tw-border tw-border-yellow-300 tw-rounded-lg tw-p-4">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="tw-text-yellow-600 tw-mt-1"
              />
              <div className="tw-flex tw-flex-col tw-gap-2">
                <div className="tw-text-sm tw-font-semibold tw-text-yellow-800">
                  {conflicts.length} tournée(s) avec conflits détectée(s)
                </div>
                <div className="tw-text-sm tw-text-yellow-700">
                  Certaines tournées contiennent plusieurs clients avec le même
                  ordre de livraison. Assignez chaque client à une tournée
                  distincte.
                </div>
              </div>
            </div>

            <div className="tw-text-smi tw-font-work tw-text-dimgray-100 tw-mb-3">
              Sélectionnez une tournée pour assigner les clients
            </div>

            <TabGroup defaultIndex={0}>
              <TabList className="tw-w-full tw-rounded-md tw-py-2 tw-px-2 tw-bg-slategray-default tw-border tw-border-solid tw-space-x-4 tw-border-[#dfe3e6]">
                {conflicts.map((conflict) => {
                  // Vérifier si toutes les assignations sont faites pour ce conflit
                  const tourAssignments = assignments[conflict.tour_key] || {};
                  const allAssigned = conflict.clients.every(
                    (client) => tourAssignments[client.client_key]
                  );

                  // Vérifier les doublons d'ordre sur une même tournée
                  const tourAssignmentsByOrder = {};
                  conflict.clients.forEach((client) => {
                    const assignedTour = tourAssignments[client.client_key];
                    if (assignedTour) {
                      if (!tourAssignmentsByOrder[assignedTour]) {
                        tourAssignmentsByOrder[assignedTour] = [];
                      }
                      tourAssignmentsByOrder[assignedTour].push(client.order);
                    }
                  });

                  const hasDuplicateOrders = Object.values(
                    tourAssignmentsByOrder
                  ).some((orders) => new Set(orders).size !== orders.length);

                  const isValid = allAssigned && !hasDuplicateOrders;

                  return (
                    <Tab
                      key={conflict.tour_key}
                      className="tw-rounded-md tw-border-0 tw-text-sm tw-py-1.5 tw-px-3 tw-bg-transparent data-[selected]:tw-bg-main data-[selected]:tw-text-white tw-inline-flex tw-items-center tw-gap-1.5"
                    >
                      <span>Tournée {conflict.tour_key}</span>
                      {!isValid && <span className="tw-text-red-500">✗</span>}
                    </Tab>
                  );
                })}
              </TabList>

              <TabPanels className="tw-mt-4">
                {conflicts.map((conflict) => (
                  <TabPanel key={conflict.tour_key}>
                    <div className="tw-border tw-border-gray-300 tw-rounded-lg tw-p-4 tw-bg-white">
                      {/* En-tête tournée */}
                      <div className="tw-flex tw-flex-col tw-gap-2 tw-mb-4 tw-pb-3 tw-border-b tw-border-gray-200">
                        <div className="tw-flex tw-items-center tw-gap-2">
                          <span className="tw-text-sm tw-font-semibold">
                            Tournée {conflict.tour_key}
                          </span>
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-4 tw-text-xs tw-text-gray-600">
                          <div className="tw-flex tw-items-center tw-gap-1">
                            <FontAwesomeIcon icon={faTruck} />
                            <span>Véhicule : {conflict.vehicle_key}</span>
                          </div>
                          <div className="tw-flex tw-items-center tw-gap-1">
                            <FontAwesomeIcon icon={faUser} />
                            <span>Chauffeur : {conflict.driver_key}</span>
                          </div>
                        </div>
                      </div>

                      {/* Résumé des erreurs d'assignation */}
                      {(() => {
                        const tourAssignments =
                          assignments[conflict.tour_key] || {};
                        const errors = [];

                        // Vérifier les clients non assignés
                        const unassignedClients = conflict.clients.filter(
                          (client) => !tourAssignments[client.client_key]
                        );
                        if (unassignedClients.length > 0) {
                          errors.push(
                            `${unassignedClients.length} client(s) non assigné(s): ${unassignedClients.map((c) => c.client_name).join(', ')}`
                          );
                        }

                        // Vérifier les doublons d'ordre sur une même tournée
                        const tourAssignmentsByOrder = {};
                        conflict.clients.forEach((client) => {
                          const assignedTour =
                            tourAssignments[client.client_key];
                          if (assignedTour) {
                            if (!tourAssignmentsByOrder[assignedTour]) {
                              tourAssignmentsByOrder[assignedTour] = [];
                            }
                            tourAssignmentsByOrder[assignedTour].push(
                              client.order
                            );
                          }
                        });

                        const hasDuplicateOrders = Object.values(
                          tourAssignmentsByOrder
                        ).some(
                          (orders) => new Set(orders).size !== orders.length
                        );

                        if (hasDuplicateOrders) {
                          errors.push(
                            'Plusieurs clients avec le même ordre assignés à la même tournée'
                          );
                        }

                        if (errors.length === 0) return null;

                        return (
                          <div className="tw-mb-4 tw-p-3 tw-bg-red-50 tw-border tw-border-red-300 tw-rounded-lg">
                            <div className="tw-text-xs tw-font-semibold tw-text-red-900 tw-mb-2">
                              ⚠️ Erreurs d'assignation
                            </div>
                            <div className="tw-flex tw-flex-col tw-gap-1 tw-text-xs tw-text-red-800">
                              {errors.map((error, idx) => (
                                <div key={idx}>• {error}</div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Instruction */}
                      <div className="tw-text-sm tw-font-medium tw-mb-3 tw-text-gray-700">
                        {conflict.clients.length} clients détectés - Assignez
                        chaque client à une tournée :
                      </div>

                      {/* Liste des clients - Regroupés par ordre */}
                      <div className="tw-flex tw-flex-col tw-gap-3">
                        {(() => {
                          // Regrouper clients par ordre
                          const clientsByOrder = {};
                          conflict.clients.forEach((client) => {
                            if (!clientsByOrder[client.order]) {
                              clientsByOrder[client.order] = [];
                            }
                            clientsByOrder[client.order].push(client);
                          });

                          return Object.entries(clientsByOrder)
                            .sort(([a], [b]) => parseInt(a) - parseInt(b))
                            .map(([order, clients]) => (
                              <div
                                key={`order-${order}`}
                                className="tw-flex tw-flex-col tw-gap-3"
                              >
                                <div className="tw-text-xs tw-font-semibold tw-text-gray-600 tw-mb-1 tw-mt-2">
                                  Ordre de livraison {order}
                                </div>
                                {clients.map((client, clientIndex) => {
                                  const isExpanded =
                                    expandedClients[
                                      `${conflict.tour_key}_${client.client_key}`
                                    ];
                                  const currentAssignment =
                                    assignments[conflict.tour_key]?.[
                                      client.client_key
                                    ];

                                  return (
                                    <div
                                      key={`client-${conflict.tour_key}-${client.client_key}`}
                                      className="tw-self-stretch tw-rounded-10xs tw-bg-whitesmoke-100 tw-box-border tw-flex tw-flex-col tw-py-2 tw-pr-[17px] tw-pl-[15px] tw-max-w-full tw-gap-3 tw-text-smi tw-text-text-dark-high-importance tw-border-[1px] tw-border-solid tw-border-border-primary-strong"
                                    >
                                      {/* Header avec infos client */}
                                      <div className="tw-flex tw-flex-row tw-items-start tw-justify-between tw-gap-4">
                                        <div className="tw-flex tw-flex-row tw-items-start tw-justify-start tw-gap-2 tw-flex-1">
                                          {/* Chevron cliquable */}
                                          <div
                                            className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-pt-1 tw-px-0 tw-pb-0 tw-cursor-pointer"
                                            onClick={() =>
                                              toggleClientExpanded(
                                                conflict.tour_key,
                                                client.client_key
                                              )
                                            }
                                          >
                                            <FontAwesomeIcon
                                              icon={
                                                isExpanded
                                                  ? faChevronDown
                                                  : faChevronRight
                                              }
                                              className="tw-text-icon-gray tw-w-4 tw-h-4"
                                            />
                                          </div>

                                          {/* Infos client */}
                                          <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-0.5">
                                            <div className="tw-relative tw-font-medium tw-text-sm tw-flex tw-items-center tw-gap-2">
                                              <span>
                                                Client ordre {client.order} :{' '}
                                                {client.client_name}
                                                {client.attached_clients
                                                  ?.length > 0 &&
                                                  ` (${client.attached_clients.map((ac) => ac.receiver_final || ac.client_name).join(', ')})`}
                                              </span>
                                              {client.attached_clients?.length >
                                                0 && (
                                                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-bg-blue-100 tw-text-blue-700 tw-text-xs tw-rounded tw-font-normal">
                                                  <FontAwesomeIcon
                                                    icon={faLink}
                                                    className="tw-w-3 tw-h-3"
                                                  />
                                                  +
                                                  {
                                                    client.attached_clients
                                                      .length
                                                  }{' '}
                                                  client(s) fusionné(s)
                                                </span>
                                              )}
                                            </div>
                                            <div className="tw-relative tw-text-text-dark-medium-importance list-item-title">
                                              Identifiant : {client.client_key}
                                            </div>
                                            <div className="tw-relative tw-text-text-dark-medium-importance list-item-title">
                                              {client.products?.length || 0}{' '}
                                              produit(s)
                                            </div>
                                          </div>
                                        </div>

                                        {/* Assignation tournée */}
                                        <div className="tw-flex tw-flex-col tw-items-end tw-justify-start tw-gap-2">
                                          <div className="tw-text-xs tw-text-text-dark-medium-importance tw-font-medium">
                                            Assigner à la tournée :
                                          </div>
                                          <select
                                            value={currentAssignment || ''}
                                            onChange={(e) =>
                                              handleAssignmentChange(
                                                conflict.tour_key,
                                                client.client_key,
                                                e.target.value
                                              )
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                            className="tw-px-3 tw-py-1.5 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md hover:tw-border-blue-400 focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-transition-all tw-bg-white"
                                          >
                                            <option value="">
                                              -- Sélectionner --
                                            </option>
                                            {Array.from(
                                              {
                                                length:
                                                  conflict.number_of_tours ||
                                                  conflict.clients.length,
                                              },
                                              (_, i) => {
                                                const tourKey = `${conflict.tour_key}_${i + 1}`;
                                                return (
                                                  <option
                                                    key={i}
                                                    value={tourKey}
                                                  >
                                                    {tourKey}
                                                  </option>
                                                );
                                              }
                                            )}
                                          </select>
                                        </div>
                                      </div>

                                      {/* Liste des produits (collapsable) */}
                                      {isExpanded && (
                                        <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-2 tw-pl-6 tw-border-t tw-border-border-primary-strong tw-pt-3">
                                          <div className="tw-relative tw-font-medium tw-text-xs tw-text-text-dark-high-importance">
                                            Produits (
                                            {client.products?.length || 0}) :
                                          </div>
                                          <div className="tw-flex tw-flex-col tw-gap-2 tw-w-full">
                                            {client.products?.map(
                                              (product, productIndex) => (
                                                <div
                                                  key={`product-${productIndex}`}
                                                  className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-py-2 tw-px-3 tw-rounded tw-border tw-bg-white tw-border-border-primary-strong"
                                                >
                                                  <div className="tw-flex tw-flex-col tw-gap-0.5">
                                                    <div className="tw-text-sm tw-font-medium tw-text-text-dark-high-importance">
                                                      {product.item_name}
                                                    </div>
                                                    <div className="tw-text-xs tw-text-text-dark-medium-importance">
                                                      Quantité :{' '}
                                                      {product.quantity ||
                                                        product.weight ||
                                                        '-'}
                                                    </div>
                                                  </div>
                                                </div>
                                              )
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ));
                        })()}
                      </div>
                    </div>
                  </TabPanel>
                ))}
              </TabPanels>
            </TabGroup>

            {/* Message d'erreur pour validation */}
            {!getAssignmentValidation().isValid && (
              <div className="tw-bg-red-50 tw-border tw-border-red-300 tw-rounded-lg tw-p-3 tw-flex tw-items-start tw-gap-2">
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="tw-text-red-600 tw-mt-0.5"
                />
                <div className="tw-text-sm tw-text-red-700">
                  {getAssignmentValidation().error}
                </div>
              </div>
            )}
          </>
        ) : showResolvedSummary ? (
          <>
            <div className="tw-flex tw-flex-col tw-items-center tw-mb-4">
              <div className="tw-bg-green-50 tw-border tw-border-green-300 tw-rounded-lg tw-p-4 tw-w-full">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <FontAwesomeIcon
                    icon={faPlus}
                    className="tw-text-green-600"
                  />
                  <div className="tw-text-sm tw-font-semibold tw-text-green-800">
                    Conflits résolus avec succès !
                  </div>
                </div>
                <div className="tw-text-sm tw-text-green-700 tw-mt-2">
                  Voici le résumé de l'importation après résolution :
                </div>
              </div>
            </div>

            <div className="tw-w-full tw-relative tw-rounded-lg tw-bg-[#f8f8f9] tw-border-[#dfe3e6] tw-border-[1px] tw-border-solid tw-box-border tw-overflow-hidden tw-flex tw-flex-col tw-items-start tw-justify-center tw-py-2 tw-px-3 tw-gap-[5px] tw-text-left tw-text-sm tw-text-[rgba(0,0,0,0.9)] tw-font-button-text">
              <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-2">
                <FontAwesomeIcon
                  icon={faPlus}
                  className="tw-w-[13px] tw-h-[13px] tw-text-[#5CB85C]"
                />
                <div className="tw-relative tw-leading-[14px] tw-text-ellipsis tw-whitespace-nowrap">
                  {resolvedResult?.tours_created != undefined
                    ? resolvedResult?.tours_created
                    : 0}{' '}
                  tournées créées
                </div>
              </div>
              <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-2">
                <FontAwesomeIcon
                  icon={faPen}
                  className="tw-w-[13px] tw-h-[13px] tw-text-[#DE9B43]"
                />
                <div className="tw-relative tw-leading-[14px] tw-text-ellipsis tw-whitespace-nowrap">
                  {resolvedResult?.tours_updated != undefined
                    ? resolvedResult?.tours_updated
                    : 0}{' '}
                  tournées modifiées
                </div>
              </div>
              <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-2">
                <FontAwesomeIcon
                  icon={faMinus}
                  className="tw-w-[13px] tw-h-[13px] tw-text-[#D9413D]"
                />
                <div className="tw-relative tw-leading-[14px] tw-text-ellipsis tw-whitespace-nowrap">
                  {resolvedResult?.tours_deleted != undefined
                    ? resolvedResult?.tours_deleted
                    : 0}{' '}
                  tournées supprimées
                </div>
              </div>
              <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-2">
                <FontAwesomeIcon
                  icon={faEyeSlash}
                  className="tw-w-[13px] tw-h-[13px] tw-text-[#4D88FF]"
                />
                <div className="tw-relative tw-leading-[14px] tw-text-ellipsis tw-whitespace-nowrap">
                  {resolvedResult?.tours_ignored != undefined
                    ? resolvedResult?.tours_ignored
                    : 0}{' '}
                  tournées ignorées
                </div>
              </div>
            </div>

            <div className="tw-flex tw-flex-col tw-items-center">
              <div className="tw-text-sm tw-font-medium tw-text-center">
                Confirmez pour appliquer les changements définitivement ou
                fermez pour annuler.
              </div>
            </div>
          </>
        ) : showNormalImport ? (
          <>
            <div className="tw-flex tw-flex-col tw-items-center">
              <div className="">
                <div className="tw-text-sm tw-font-medium">
                  Votre fichier a été importé et traité. Un résumé provisoire de
                  l'opération est disponible ci-dessous.
                </div>
                <div className="tw-mt-4 tw-text-sm tw-font-medium">
                  Confirmez pour appliquer les changements ou fermez la fenêtre
                  pour revenir à l'étape précédente.
                </div>
              </div>
            </div>

            <div className="tw-w-full tw-relative tw-rounded-lg tw-bg-[#f8f8f9] tw-border-[#dfe3e6] tw-border-[1px] tw-border-solid tw-box-border tw-overflow-hidden tw-flex tw-flex-col tw-items-start tw-justify-center tw-py-2 tw-px-3 tw-gap-[5px] tw-min-w-[447px] tw-text-left tw-text-sm tw-text-[rgba(0,0,0,0.9)] tw-font-button-text">
              <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-2">
                <FontAwesomeIcon
                  icon={faPlus}
                  className="tw-w-[13px] tw-h-[13px] tw-text-[#5CB85C]"
                />
                <div className="tw-relative tw-leading-[14px] tw-text-ellipsis tw-whitespace-nowrap">
                  {uploadResult?.tours_created != undefined
                    ? uploadResult?.tours_created
                    : 0}{' '}
                  tournées créée
                </div>
              </div>
              <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-2">
                <FontAwesomeIcon
                  icon={faPen}
                  className="tw-w-[13px] tw-h-[13px] tw-text-[#DE9B43]"
                />
                <div className="tw-relative tw-leading-[14px] tw-text-ellipsis tw-whitespace-nowrap">
                  {uploadResult?.tours_updated != undefined
                    ? uploadResult?.tours_updated
                    : 0}{' '}
                  tournées modifiées
                </div>
              </div>
              <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-2">
                <FontAwesomeIcon
                  icon={faMinus}
                  className="tw-w-[13px] tw-h-[13px] tw-text-[#D9413D]"
                />
                <div className="tw-relative tw-leading-[14px] tw-text-ellipsis tw-whitespace-nowrap">
                  {uploadResult?.tours_deleted != undefined
                    ? uploadResult?.tours_deleted
                    : 0}{' '}
                  tournées supprimés
                </div>
              </div>
              <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-2">
                <FontAwesomeIcon
                  icon={faEyeSlash}
                  className="tw-w-[13px] tw-h-[13px] tw-text-[#4D88FF]"
                />
                <div className="tw-relative tw-leading-[14px] tw-text-ellipsis tw-whitespace-nowrap">
                  {uploadResult?.tours_ignored != undefined
                    ? uploadResult?.tours_ignored
                    : 0}{' '}
                  tournées ignorées
                </div>
              </div>
              <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-2">
                <FontAwesomeIcon
                  icon={faPlus}
                  className="tw-w-[13px] tw-h-[13px] tw-text-[#5CB85C]"
                />
                <div className="tw-relative tw-leading-[14px] tw-text-ellipsis tw-whitespace-nowrap">
                  {uploadResult?.vehicle_created != undefined
                    ? uploadResult?.vehicle_created
                    : 0}{' '}
                  véhicules créée
                </div>
              </div>
              <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-2">
                <FontAwesomeIcon
                  icon={faPlus}
                  className="tw-w-[13px] tw-h-[13px] tw-text-[#5CB85C]"
                />
                <div className="tw-relative tw-leading-[14px] tw-text-ellipsis tw-whitespace-nowrap">
                  {uploadResult?.driver_created != undefined
                    ? uploadResult?.driver_created
                    : 0}{' '}
                  chauffeurs créée
                </div>
              </div>
            </div>
            <ImportIssuesList
              title="Lignes en erreur (leur tournée est ignorée)"
              issues={uploadResult?.errors}
              tone="error"
            />
            <ImportIssuesList
              title="Avertissements"
              issues={uploadResult?.warnings}
              tone="warning"
            />
          </>
        ) : null}

        {error && (
          <div className="tw-flex tw-flex-col">
            <div className="tw-text-sm tw-text-red-500">{error}</div>
          </div>
        )}
      </div>
    </GenericModal>
  );
}
